/**
 * ============================================================================
 * Certifications — facts from the content layer, presentation from this file.
 * ============================================================================
 *
 * Transitional bridge (Constitution V.4; precedent `constants/experience.ts`,
 * D-006). Credential *facts* — name, issuer, issue date, credential ID — come
 * from `src/content/data/certifications.json` through the typed loader. To
 * correct a date or an issuer, edit the JSON, not this file.
 *
 * What stays here is presentation, not career fact, so it does not violate
 * Article II.2: which credentials the section curates, how they are grouped and
 * tiered, which roles they map to, and the scanned certificate images (the
 * content schema has no field for a `files[]` asset).
 *
 * The tier split is deliberate and predates this bridge: accredited credentials
 * are shown above, and weighted more heavily than, courses and workshops.
 * Mixing the two at equal visual weight dilutes the strong credentials — a
 * reader who spots filler discounts the whole list. The content layer carries no
 * tier field and deliberately still does not: which tier a credential belongs to
 * is an editorial judgement about how to PRESENT it, not a fact about it, so it
 * stays here rather than bumping the content schema (IV.6) to record an opinion.
 *
 * THE CURATION IS NOW COMPLETE, AND THAT IS A CHANGE OF KIND (D-065).
 * Until 2026-08-19 this list was a 12-card sample of a 27-entry layer, and the
 * nine verified credentials it omitted rendered NOWHERE — not on the page, not in
 * the pre-rendered HTML, not to a search engine. Every one of the 21 verified
 * entries now has a card. The gap was invisible from every direction that gets
 * checked: the publish gate was correct, the loader was correct, the bridge was
 * correct, and the section still showed 43% of the credentials. **A publish gate
 * decides what is ELIGIBLE; this array decides what is RENDERED**, and only the
 * first of those two had a test.
 *
 * So the standing rule for this file: adding an entry to `certifications.json`
 * does not put it on the page. `CARDS` must gain a spec in the same pass, and the
 * count assertion in the verification sweep is what catches it when it does not.
 */

import {
	AWS_Online_India_Summit_2025_Certificate,
	DPDPA_Certificate,
	LSS_BlackBelt_Certificate,
	LSS_GreenBelt_Certificate,
	Kaizen_Certificate,
	IIT_Roorkee_Analytics_Certificate,
	PMP_Certificate,
	MERN_Stack_Certificate,
	Azure_Skynet_Ethical_Hacking_Certificate,
	Hardware_A_Plus_Certificate,
	Anthropic_AI_Fluency_Certificate,
	Anthropic_Claude_101_Certificate,
	Anthropic_Claude_Code_101_Certificate,
	Anthropic_Claude_Code_In_Action_Certificate,
	Anthropic_Claude_Cowork_Certificate,
	Scrum_Alliance_CSPO_Certificate,
	Scrum_Alliance_CSM_Certificate,
	Scrum_Alliance_AI_Agility_Certificate,
	MongoDB_CRUD_Python_Certificate,
	LinkedIn_Learning_OpEx_Kaizen_Certificate,
	ISOEH_Ethical_Hacking_Kali_Certificate,
	ISOEH_Advance_Ethical_Hacking_Certificate,
	ISOEH_Digital_Forensic_Certificate
} from "../assets";
import { certifications } from "../content";
import type { Certification } from "../types/content";

export type CertCategory =
	| "Compliance"
	| "Process"
	| "AI"
	| "Analytics"
	| "Cloud"
	| "Development"
	| "Security"
	| "Hardware"
	| "Management";

/**
 * Display order for the filter row. Declared here so the union and the order it
 * renders in stay in one place; the section shows only the entries that have at
 * least one card behind them, so a category may sit in this list unused.
 */
export const CATEGORY_ORDER: CertCategory[] = [
	"Compliance",
	"Process",
	"AI",
	"Analytics",
	"Cloud",
	"Development",
	"Security",
	"Hardware",
	"Management"
];

/**
 * Accredited credentials are shown above, and weighted more heavily than,
 * courses and workshops. Mixing the two at equal visual weight dilutes the
 * strong credentials — a reader who spots filler discounts the whole list.
 */
export type CertTier = "certification" | "training";

export type CertStatus = "Active" | "Lifetime";

export interface CertFile {
	label: string;
	src: string;
	issuedDate?: string;
	duration?: string;
	credentialId?: string;
}

export interface NonCredlyCert {
	id: string;
	title: string;
	issuer: string;
	category: CertCategory;
	tier: CertTier;
	/** Drives timeline grouping. One card per credential, never one per file. */
	year: number;
	status?: CertStatus;
	/** External verification. Cards with no local `files` link here instead of opening the viewer. */
	verifyLink?: string;
	auditCritical?: boolean;
	/**
	 * Editorial mapping of credential → the roles it was applied in. **Rendered
	 * nowhere** — it is carried through the resolver and dropped. Optional since
	 * D-065 rather than populated for the sake of it: the nine credentials added
	 * that day have no owner-supplied mapping, and inventing one to fill a field
	 * no visitor sees would be manufacturing career fact (II.2). Either surface it
	 * or delete it — open in docs/TODO.md.
	 */
	usedInRole?: string[];
	/** Scanned certificate images. May be empty when only external verification exists. */
	files: CertFile[];
}

/*
 * `CREDLY_PROFILE` USED TO LIVE HERE AND IT WAS A LATENT-LINK DEFECT (D-091).
 * CSM and CSPO both carried `verifyLink: CREDLY_PROFILE`, so both cards said
 * "Verify credential" and landed on
 * `credly.com/users/<slug>/badges/credly` — which was MEASURED on 2026-08-28
 * and lists 12 badges, none of them CSM or CSPO. The three Scrum Alliance
 * credentials sit on that profile's OTHER tab, unselected, and they are
 * self-declared entries carrying no link of their own. So the promise those
 * two cards made was one this section could not keep, exactly as the comment
 * on `ai-agility` below predicted when it declined to extend the pattern to a
 * third card.
 *
 * Both now resolve through the layer's `credentialUrl` instead, read off the
 * QR code the issuer printed on each certificate. That is the same route
 * `mongodb-crud` and `ai-agility` take, and it keeps the link a content-layer
 * FACT rather than a curated copy of one that can drift.
 *
 * The Credly profile is still linked, twice — the hero CTA and this section's
 * own footer — where a profile page is the right destination. What it is not
 * is evidence for a specific credential.
 */

/** Presentation spec for one card. `sources` names the content-layer entries whose facts drive it. */
interface CardSpec {
	id: string;
	/** Content-layer ids. Empty only for a credential the layer does not carry yet. */
	sources: string[];
	/** Set only when a card deliberately spans several credentials, or has no source. */
	title?: string;
	/** Set only for a card with no content-layer source. */
	issuer?: string;
	/** Set only for a card with no content-layer source. */
	year?: number;
	category: CertCategory;
	tier: CertTier;
	status?: CertStatus;
	verifyLink?: string;
	auditCritical?: boolean;
	usedInRole?: string[];
	files: Array<{ label: string; src: string; duration?: string; source?: string }>;
}

const CARDS: CardSpec[] = [
	/* ===================== ACCREDITED CERTIFICATIONS ====================== */
	{
		/* No `verifyLink`: the resolver falls through to the layer's
		   `credentialUrl`, which is the issuer's own authentication page for THIS
		   credential (2163826). See the note where CREDLY_PROFILE used to be. */
		id: "csm",
		sources: ["csm"],
		category: "Management",
		tier: "certification",
		status: "Active",
		usedInRole: ["Delivery Lead", "Technical Project Manager"],
		files: [{ label: "CSM Certificate", src: Scrum_Alliance_CSM_Certificate }]
	},
	{
		/* Same as CSM above, and with a different certification id (2172184), which
		   is what structurally rules out the CSPO-to-CSM mislink the layer's note
		   warned about rather than merely avoiding it. */
		id: "cspo",
		sources: ["cspo"],
		category: "Management",
		tier: "certification",
		status: "Active",
		usedInRole: ["Product Owner", "Delivery Lead"],
		files: [{ label: "CSPO Certificate", src: Scrum_Alliance_CSPO_Certificate }]
	},
	{
		/* This comment used to say the profile link was unconfirmed and that
		   extending it to a third card would be worse than no link at all. D-091
		   checked it: the profile's Credly tab does NOT carry this credential, or
		   CSM, or CSPO. Declining the pattern was right, and CSM and CSPO have now
		   been moved off it too. This card resolves through the layer's
		   `credentialUrl` — bcert.me/srkkygkxq, loaded and read on 2026-08-28: it
		   names the owner and this exact credential. */
		id: "ai-agility",
		sources: ["ai-agility"],
		category: "AI",
		tier: "certification",
		files: [{ label: "Certificate of Completion", src: Scrum_Alliance_AI_Agility_Certificate }]
	},
	{
		/* The most recent credential on the page, and one of the entries carrying a
		   public verification URL — which is precisely what this section is short of.
		   THIS COMMENT USED TO SAY "one of only TWO", and it was exactly true the day
		   it was written (D-065). It was false by D-090, false again by D-091, and
		   false a fourth time when `pmi-genai` gained a URL in D-096, by which point
		   the layer carried seven. A count in a comment is a claim nothing checks,
		   and it goes stale on the next good day's work; the layer can settle this
		   one, so the comment declines to. `verifyLink` is left unset on purpose: the
		   resolver falls back to the layer's `credentialUrl`, so the link stays a
		   content-layer fact rather than a copy of one that can drift out of step. */
		id: "mongodb-crud",
		sources: ["mongodb-crud-python"],
		category: "Development",
		tier: "certification",
		files: [{ label: "MongoDB Certificate", src: MongoDB_CRUD_Python_Certificate }]
	},
	{
		/* Moved from the training tier in D-065 on the owner's tier spec, which named
		   it among the accredited credentials.

		   THIS CARD RENDERED AN EMPTY BOX UNTIL D-096. `NonCredlyCertifications`
		   draws file chips when `files` is non-empty and a "Verify credential" link
		   when `verifyLink` resolves, and this spec had neither — `files: []` and a
		   layer entry whose `credentialUrl` was null — so the card printed its title
		   and its issuer over nothing. Not a defect in either file, which is what
		   made it survive: both were behaving correctly over the data they had.

		   The owner supplied the Credly badge URL on 2026-08-30 and it is now the
		   layer's `credentialUrl`, so this card verifies externally like `csm`,
		   `cspo`, `ai-agility` and `mongodb-crud` do. No `verifyLink` here, on
		   D-091's terms: a curated copy of a link the layer already owns is a second
		   place for it to drift.

		   `files` STAYS EMPTY, and that is its finished state rather than a gap. No
		   PMI artifact exists for it — the two PDFs in D-090's `project-management/`
		   drop were byte-identical to each other and were LinkedIn Learning's, not
		   PMI's, so nothing in that drop was evidence for this credential. */
		id: "genai-pm",
		sources: ["pmi-genai"],
		category: "Management",
		tier: "certification",
		usedInRole: ["AI Governance", "Project Manager"],
		files: []
	},
	{
		/**
		 * The one card with no content-layer entry: `certifications.json` has no
		 * DPDPA record, and its issuer here ("Compliance Programme") is a
		 * description rather than an issuing body, so it cannot be added without
		 * inventing one. Kept rendering deliberately — dropping a real,
		 * audit-relevant credential as a side effect of rewiring would be a
		 * regression. Tracked as an open content item in docs/TODO.md.
		 */
		id: "dpdpa",
		sources: [],
		title: "Digital Personal Data Protection Act (DPDPA)",
		issuer: "Compliance Programme",
		year: 2025,
		category: "Compliance",
		tier: "certification",
		status: "Active",
		auditCritical: true,
		usedInRole: ["Data Protection", "Governance"],
		files: [{ label: "DPDPA Certificate", src: DPDPA_Certificate }]
	},
	{
		/* One merged "Lean Six Sigma Green & Black Belt" card until D-065, split on
		   the owner's ruling that every card carries its credential's EXACT layer
		   name. The merge was good design and it cost something real: the strings
		   "CLSSBB" and "CLSSGB" — post-nominals the owner carries in their own
		   LinkedIn slug — appeared NOWHERE in the HTML a recruiter searches. Two
		   ids, two issue dates, two belts; two cards. */
		id: "clssbb",
		sources: ["clssbb"],
		category: "Process",
		tier: "certification",
		status: "Lifetime",
		usedInRole: ["Technical Project Manager", "Delivery Lead"],
		files: [{ label: "Black Belt", src: LSS_BlackBelt_Certificate, duration: "6 Months" }]
	},
	{
		id: "clssgb",
		sources: ["clssgb"],
		category: "Process",
		tier: "certification",
		status: "Lifetime",
		usedInRole: ["Technical Project Manager", "Delivery Lead"],
		files: [{ label: "Green Belt", src: LSS_GreenBelt_Certificate, duration: "6 Months" }]
	},
	{
		id: "kaizen",
		sources: ["ckp"],
		category: "Process",
		tier: "certification",
		status: "Lifetime",
		usedInRole: ["Process Improvement Lead"],
		files: [{ label: "Kaizen Certification", src: Kaizen_Certificate }]
	},
	{
		/* OWNER-RULED tier, 2026-08-19, not an inferred one: this is a preparation
		   course issued by ISEL Global rather than the PMP itself, so neither the
		   issuer nor the title derives the answer. Moved up from training. */
		id: "pmp",
		sources: ["pmp-prep"],
		category: "Management",
		tier: "certification",
		usedInRole: ["Project Manager"],
		files: [{ label: "PMP Preparation Certificate", src: PMP_Certificate, duration: "35 Contact Hours" }]
	},
	{
		/* OWNER-RULED tier, 2026-08-19. Its layer entry records a duplicate mirrored
		   on LinkedIn under issuer "LinkedIn", which is what made the tier ambiguous
		   enough to need a ruling rather than a guess. */
		id: "opex-kaizen",
		sources: ["opex-workout-kaizen"],
		category: "Process",
		tier: "certification",
		files: [{ label: "Certificate of Completion", src: LinkedIn_Learning_OpEx_Kaizen_Certificate }]
	},
	{
		/* OWNER-RULED tier, 2026-08-19 — NPTEL Online Certification (Elite), a
		   12-week IIT Roorkee course settled by a proctored exam, which is a
		   different thing from a MOOC completion.
		   THE SCAN BELOW USED TO HANG ON THE COURSERA CARD (D-065), and it is
		   unambiguously this credential: course title, roll number
		   NPTEL23MG14S63961547 and Jan-Apr 2023 all match THIS entry and none of them
		   match Coursera's. It rendered under a card titled "Business Analytics" and
		   issued by Coursera — one issuing body's certificate displayed under
		   another's name, in the section whose entire premise is that its evidence
		   checks out. */
		id: "iit-roorkee",
		sources: ["iit-roorkee-analytics"],
		category: "Analytics",
		tier: "certification",
		files: [{ label: "NPTEL Elite Certificate", src: IIT_Roorkee_Analytics_Certificate, duration: "12 Weeks" }]
	},

	/* ======================== TRAINING & WORKSHOPS ========================= */
	{
		/* THE FIVE ANTHROPIC COURSES, AND WHY THEY WERE NOT HERE FOR A FORTNIGHT.
		   Their layer entries have carried `"category": "ai"` since D-014 and it
		   did nothing, because the filter reads THIS file's `category`, not the
		   content layer's — the same gap D-065 recorded, one field further in.
		   What actually held them was the publish gate: D-039 moved all five to
		   `pending-details` under the owner rule that verified requires a
		   credentialUrl, and Anthropic mints neither a URL nor an ID. The owner
		   supplied the certificates on 2026-08-27, which is the independent
		   artifact D-039 was really asking for, and the entries flipped.

		   TIER IS `training`, deliberately. They are Certificates of Completion
		   for self-paced courses, which is exactly what the tier blurb below
		   describes; filing them beside CSM and the Six Sigma belts would be the
		   dilution the tier split exists to prevent. Tier is an editorial
		   judgement about presentation and stays here rather than in the layer
		   (see the header). CATEGORY is `AI`, which is what the brief asked for
		   and what puts them behind the AI pill. */
		id: "anthropic-ai-fluency",
		sources: ["anthropic-ai-fluency"],
		category: "AI",
		tier: "training",
		files: [{ label: "Certificate of Completion", src: Anthropic_AI_Fluency_Certificate }]
	},
	{
		id: "anthropic-claude-101",
		sources: ["anthropic-claude-101"],
		category: "AI",
		tier: "training",
		files: [{ label: "Certificate of Completion", src: Anthropic_Claude_101_Certificate }]
	},
	{
		id: "anthropic-claude-code-101",
		sources: ["anthropic-claude-code-101"],
		category: "AI",
		tier: "training",
		files: [{ label: "Certificate of Completion", src: Anthropic_Claude_Code_101_Certificate }]
	},
	{
		id: "anthropic-claude-code-in-action",
		sources: ["anthropic-claude-code-in-action"],
		category: "AI",
		tier: "training",
		files: [{ label: "Certificate of Completion", src: Anthropic_Claude_Code_In_Action_Certificate }]
	},
	{
		id: "anthropic-claude-cowork",
		sources: ["anthropic-claude-cowork"],
		category: "AI",
		tier: "training",
		files: [{ label: "Certificate of Completion", src: Anthropic_Claude_Cowork_Certificate }]
	},
	{
		id: "aws-summit",
		sources: ["aws-summit-india-2025"],
		category: "Cloud",
		tier: "training",
		usedInRole: ["Cloud Architecture"],
		files: [{ label: "Summit Certificate", src: AWS_Online_India_Summit_2025_Certificate }]
	},
	{
		/* Category was "Process" until D-065, which filed a course literally called
		   Business Analytics outside the Analytics filter — a visitor filtering for
		   exactly this credential did not find it. `files` is empty because the scan
		   that used to sit here belongs to `iit-roorkee` above, and no Coursera scan
		   exists.

		   THIS COMMENT ASSERTED AN EMPTY STATE THAT STOPPED BEING TRUE. It read "this
		   entry carries no `credentialUrl`, so the card offers no evidence, which is
		   the correct state" — accurate when written, and it described the D-096
		   EMPTY-BOX defect without naming it: `files: []` and no resolvable link is a
		   card printing a title and an issuer over nothing, which is what `genai-pm`
		   did for a fortnight. The owner supplied the specialization share link on
		   2026-09-02 and it is the layer's `credentialUrl` now, so this card verifies
		   externally instead. No `verifyLink` here, on D-091's terms — a curated copy
		   of a link the layer already owns is a second place for it to drift. */
		id: "business-analytics",
		sources: ["coursera-business-analytics"],
		category: "Analytics",
		tier: "training",
		usedInRole: ["Stakeholder Reporting"],
		files: []
	},
	{
		/* The resolver picks the layer's `credentialUrl` up the same way it does for
		   `mongodb-crud`. THIS COMMENT USED TO OPEN "the other of the layer's only two
		   entries with a public credentialUrl", which was a count in a comment sitting
		   two specs below the `mongodb-crud` note explaining why counts in comments go
		   stale. The layer carried seven such entries by D-096 and carries nine now. */
		id: "coursera-accounting",
		sources: ["coursera-data-analytics-accounting"],
		category: "Analytics",
		tier: "training",
		files: []
	},
	{
		/* Verifies through the layer like the two Coursera cards above. Its link is
		   the one addition of 2026-09-02 that was NOT fetch-verified — udemy.com
		   answers a non-browser request with a 403 bot challenge — so the layer's note
		   records it as unfetched rather than as checked. The join that IS exact is
		   internal: the URL's path segment is character-for-character the
		   `credentialId` this entry has carried since D-014. */
		id: "udemy-business-analysis",
		sources: ["udemy-business-analysis-masterclass"],
		category: "Analytics",
		tier: "training",
		files: []
	},
	{
		/* Three ISOEH entries, each with its own credential ID, and each recorded in
		   the layer as a Certificate of Participation in a workshop rather than a
		   certification course — which is what the training tier is for. Their
		   OW-prefix ruling is still open in docs/TODO.md; it affects how they are
		   LABELLED, not whether they belong here.

		   ALL THREE CARRIED `files: []` UNTIL D-092, AND THE SCANS WERE IN THE
		   REPOSITORY THE WHOLE TIME. Three cards asserted a credential, offered
		   nothing to look at, and carry no `credentialUrl` either — so they were
		   the only cards in this section with no evidence of any kind, while
		   their evidence sat tracked in `src/assets` rendering nowhere. That is
		   D-065 in its purest form and it is why they are wired here rather than
		   quarantined: each scan carries two third parties' handwritten signature
		   specimens, which is a reason to REDACT, not a reason to withhold the
		   credential. The published derivatives mask both specimens and keep both
		   typed signatory names, both qualification lines, the workshop date and
		   the owner's own registration identifier. */
		id: "isoeh-kali",
		sources: ["ethical-hacking-kali"],
		category: "Security",
		tier: "training",
		files: [{ label: "Certificate of Participation", src: ISOEH_Ethical_Hacking_Kali_Certificate }]
	},
	{
		id: "isoeh-advance-kali",
		sources: ["isoeh-advance-ethical-hacking-kali"],
		category: "Security",
		tier: "training",
		files: [{ label: "Certificate of Participation", src: ISOEH_Advance_Ethical_Hacking_Certificate }]
	},
	{
		id: "isoeh-forensic",
		sources: ["isoeh-digital-forensic-workshop"],
		category: "Security",
		tier: "training",
		files: [{ label: "Certificate of Participation", src: ISOEH_Digital_Forensic_Certificate }]
	},
	{
		/* Title override removed in D-065: it read "Ethical Hacking Workshop" where
		   the credential is issued as "Ethical Hacking". */
		id: "ethical-hacking",
		sources: ["azure-skynet-ethical-hacking"],
		category: "Security",
		tier: "training",
		usedInRole: ["Security Awareness"],
		files: [{ label: "Ethical Hacking Certificate", src: Azure_Skynet_Ethical_Hacking_Certificate }]
	},
	{
		/* Title override removed in D-065: it read "MERN Stack Development" where the
		   credential is issued as "Certificate in MERN Stack Development". */
		id: "mern",
		sources: ["cloudnet-mern"],
		category: "Development",
		tier: "training",
		usedInRole: ["Full Stack Engineer"],
		files: [{ label: "MERN Stack Certificate", src: MERN_Stack_Certificate }]
	},
	{
		id: "hardware",
		sources: ["cloudnet-hardware-a-plus"],
		category: "Hardware",
		tier: "training",
		usedInRole: ["System Support Engineer"],
		files: [{ label: "Hardware A+ Certificate", src: Hardware_A_Plus_Certificate }]
	}
];

const BY_ID = new Map<string, Certification>(certifications.map((c: Certification) => [c.id, c]));

/** "YYYY-MM" → 2025. Returns 0 when the entry is missing a date, so `Math.max` stays safe. */
const yearOf = (cert?: Certification): number => (cert?.issueDate ? Number(cert.issueDate.slice(0, 4)) : 0);

/**
 * A spec naming a content-layer id that no longer exists would silently render a
 * blank card, so it fails loudly in development instead. Production keeps the
 * card and degrades to the spec's own values rather than blanking the section.
 */
const resolve = (specId: string, sourceId: string): Certification | undefined => {
	const cert = BY_ID.get(sourceId);
	/* Was `process.env.NODE_ENV !== "production"` until D-042. `process` does not
	   exist in a browser — CRA's webpack define plugin substituted the literal at
	   build time, and Vite makes no such substitution, so the CRA form would throw
	   a ReferenceError on the first unresolved id rather than logging one.
	   `import.meta.env.PROD` is the direct equivalent and is statically replaced
	   the same way, so this branch is still dead-code-eliminated in production. */
	if (!cert && !import.meta.env.PROD) {
		// eslint-disable-next-line no-console
		console.error(`nonCredlyCerts: card "${specId}" references unknown certification id "${sourceId}".`);
	}
	return cert;
};

export const nonCredlyCerts: NonCredlyCert[] = CARDS.map((spec: CardSpec): NonCredlyCert => {
	const sources = spec.sources.map((id: string) => resolve(spec.id, id));
	const primary = sources[0];
	const years = sources.map(yearOf).filter((y: number) => y > 0);

	return {
		id: spec.id,
		title: spec.title ?? primary?.name ?? spec.id,
		issuer: spec.issuer ?? primary?.issuer ?? "",
		category: spec.category,
		tier: spec.tier,
		year: spec.year ?? (years.length ? Math.max(...years) : 0),
		status: spec.status,
		/* A card with no curated link still verifies externally when its credential
		   carries a public URL, so the layer's `credentialUrl` is the fallback. */
		verifyLink: spec.verifyLink ?? primary?.credentialUrl ?? undefined,
		auditCritical: spec.auditCritical,
		usedInRole: spec.usedInRole,
		files: spec.files.map(({ label, src, duration, source }) => {
			const cert = source ? resolve(spec.id, source) : primary;
			return {
				label,
				src,
				duration,
				issuedDate: cert?.issueDate ?? undefined,
				credentialId: cert?.credentialId ?? undefined
			};
		})
	};
});
